import { useRef, useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { LSTORAGE_KEY, SESSION_KEY } from 'src/constants';
import { LocalStore, SessionStore } from 'src/utils';
import { mergeOverride } from 'src/utils/array';

export function useViewTime(options: any = {}) {
    const { threshold = 0.3, onFinish, contentId, tracking } = options;
    const { ref, inView } = useInView({ threshold, triggerOnce: false });
    const startRef = useRef(0);
    const totalRef = useRef(0);

    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                if (!startRef.current && inView) {
                    startRef.current = Date.now();
                }
            } else {
                if (startRef.current) {
                    totalRef.current += Date.now() - startRef.current;
                    startRef.current = 0;
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [inView]);

    useEffect(() => {
        if (inView) {
            if (!startRef.current) {
                startRef.current = Date.now();
            } else {
                totalRef.current = Date.now() - startRef.current;
                startRef.current = 0;
            }
        } else {
            if (startRef.current) {
                totalRef.current += Date.now() - startRef.current;
                startRef.current = 0;
            }
        }
    }, [inView, contentId]);

    useEffect(() => {
        totalRef.current = 0;
        return () => {
            if (startRef.current) {
                totalRef.current += Date.now() - startRef.current;
                startRef.current = 0;
            }
        };
    }, [contentId]);

    const getViewTime = useCallback(() => {
        let total = totalRef.current;
        if (startRef.current) {
            total += Date.now() - startRef.current;
        }

        return total;
    }, []);

    useEffect(() => {
        return () => {
            const total = getViewTime();
            if ((total < 100 && tracking != 'page') || !contentId) {
                return;
            }
            const data = LocalStore.get(LSTORAGE_KEY.VIEW_DETAIL) || {};
            const ssid = SessionStore.get(SESSION_KEY.SSID);
            let strUserId = LocalStore.get(LSTORAGE_KEY.BM_UID) || (window as any).dataAuth?.strUserId;

            const object = {
                [contentId]: {
                    uid: strUserId,
                    value: contentId,
                    session_id: strUserId ? strUserId + '_' + ssid : ssid,
                    sections: [
                        {
                            section: tracking,
                            spent_time: Math.round(total / 1000),
                        },
                    ],
                },
            };

            const _data = mergeOverride(data, object);
            LocalStore.set(LSTORAGE_KEY.VIEW_DETAIL, _data);
            if (onFinish) {
                onFinish(total);
            }
        };
    }, [getViewTime, onFinish]);

    return { ref };
}

export default useViewTime;
